import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Calendar, User, Clock } from "lucide-react";
import { getPublishedBlogs, PublicBlog } from "../../../services/blog.service";

const CATEGORIES = ["All", "Buying Guide", "Selling Tips", "Industry News", "Finance", "Reviews"];

const categoryColors: Record<string, string> = {
  "Buying Guide": "bg-[#1E6DFF]/10 text-[#1E6DFF]",
  "Selling Tips": "bg-[#22C55E]/10 text-[#22C55E]",
  "Industry News": "bg-[#F59E0B]/10 text-[#F59E0B]",
  "Finance": "bg-[#EF4444]/10 text-[#EF4444]",
  "Reviews": "bg-[#8B5CF6]/10 text-[#8B5CF6]",
};

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const formatAuthor = (name: string | null) => {
  if (!name) return "MORROCARS Editorial";
  const parts = String(name).split(/\s+/).filter(Boolean);
  const clean = parts.filter((p) => !p.includes("==")).join(" ");
  if (clean && clean.length <= 40) return clean;
  return "MORROCARS Editorial";
};

const extractText = (node: any): string => {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node.text) return node.text;
  if (Array.isArray(node.content)) {
    return node.content.map(extractText).join(" ");
  }
  return "";
};

const estimateReadTime = (blog: PublicBlog) => {
  try {
    const content = (blog as any).Content;
    const plainText = content ? extractText(content).trim() : `${blog.Title} ${blog.Excerpt || ""}`.trim();
    const words = plainText.length > 0 ? plainText.split(/\s+/).length : 0;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  } catch {
    return "1 min read";
  }
};

export function Blog() {
  const [blogs, setBlogs] = useState<PublicBlog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    getPublishedBlogs()
      .then(setBlogs)
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, []);

  const categoryBlogs =
    selectedCategory === "All"
      ? blogs
      : blogs.filter(
          (b) => (b.Category || "").toLowerCase() === selectedCategory.toLowerCase()
        );

  const featured =
    categoryBlogs.find((b) => b.IsFeatured) ||
    (selectedCategory === "All" ? categoryBlogs[0] : undefined);
  const rest = featured
    ? categoryBlogs.filter((b) => b.id !== featured.id)
    : categoryBlogs;

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-[#2D323A] to-[#1E2229] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl text-white font-bold mb-3">MORROCARS Blog</h1>
          <p className="text-white/70 text-base">Expert advice, buying guides, and automotive news</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Categories Bar */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {CATEGORIES.map((c) => {
            const isActive = c === selectedCategory;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setSelectedCategory(c)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive
                    ? "bg-[#1E6DFF] text-white shadow-sm"
                    : "bg-[#F5F7FA] text-[#717182] hover:bg-[#E5E7EB]"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-[#1E6DFF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-[#717182] text-sm">Loading articles…</p>
          </div>
        ) : categoryBlogs.length === 0 ? (
          <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-[#717182] text-base font-medium">
              No articles found{selectedCategory !== "All" ? ` in "${selectedCategory}"` : ""}.
            </p>
            {selectedCategory !== "All" && (
              <button
                type="button"
                onClick={() => setSelectedCategory("All")}
                className="mt-3 text-sm text-[#1E6DFF] hover:underline font-medium cursor-pointer"
              >
                View all articles
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Featured Post (Hero Card) */}
            {featured && (
              <Link to={`/blog/${featured.Slug}`} className="block mb-10 group">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-2xl overflow-hidden border border-[#E5E7EB] hover:shadow-xl transition-shadow">
                  {featured.CoverImage ? (
                    <div className="overflow-hidden h-64 lg:h-full min-h-[260px]">
                      <img
                        src={featured.CoverImage}
                        alt={featured.Title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-64 lg:h-full min-h-[260px] bg-[#F5F7FA] flex items-center justify-center text-gray-400 text-sm font-medium">
                      MORROCARS
                    </div>
                  )}

                  <div className="p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={`text-xs font-medium px-2.5 py-0.5 rounded-lg ${
                          categoryColors[featured.Category] || "bg-[#F5F7FA] text-[#717182]"
                        }`}
                      >
                        {featured.Category || "Buying Guide"}
                      </span>
                      <span className="text-xs font-semibold px-2.5 py-0.5 bg-[#1E6DFF] text-white rounded-lg">
                        Featured
                      </span>
                    </div>

                    <h2 className="text-2xl font-bold text-[#2D323A] mb-3 group-hover:text-[#1E6DFF] transition-colors leading-snug">
                      {featured.Title}
                    </h2>

                    <p className="text-[#717182] mb-4 text-sm leading-relaxed line-clamp-3">
                      {featured.Excerpt}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-[#717182] mb-5">
                      <span className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" />
                        {formatAuthor(featured.CreatedBy)}
                      </span>
                      {featured.PublishedAt && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(featured.PublishedAt)}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {estimateReadTime(featured)}
                      </span>
                    </div>

                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[#1E6DFF]">
                      Read Article <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {/* Post Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.Slug}`}
                    className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-shadow group flex flex-col"
                  >
                    <div className="overflow-hidden h-44 bg-[#F5F7FA]">
                      {post.CoverImage ? (
                        <img
                          src={post.CoverImage}
                          alt={post.Title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-medium">
                          MORROCARS
                        </div>
                      )}
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      <div className="mb-3">
                        <span
                          className={`text-xs font-medium px-2.5 py-0.5 rounded-lg inline-block ${
                            categoryColors[post.Category] || "bg-[#F5F7FA] text-[#717182]"
                          }`}
                        >
                          {post.Category || "Buying Guide"}
                        </span>
                      </div>

                      <h3 className="text-[#2D323A] font-bold text-base mb-2 group-hover:text-[#1E6DFF] transition-colors leading-snug line-clamp-2">
                        {post.Title}
                      </h3>

                      <p className="text-sm text-[#717182] mb-4 line-clamp-2 leading-relaxed flex-1">
                        {post.Excerpt}
                      </p>

                      <div className="flex flex-wrap items-center gap-2.5 text-xs text-[#717182] pt-3 border-t border-gray-100">
                        <span className="truncate max-w-[120px]">{formatAuthor(post.CreatedBy)}</span>
                        <span>·</span>
                        <span>{formatDate(post.PublishedAt)}</span>
                        <span>·</span>
                        <span>{estimateReadTime(post)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
